import { useQuery, useMutation, useSubscription } from "urql";
import { useState, useCallback, useEffect, useTransition, useRef } from "react";
import {
  txNotificationsQuery,
  markNotificationsAsReadMutation,
  unreadNotificationCountQuery,
  txNotificationSubscription,
} from "../support/mutations";
import { NOTIFICATION_STATUS } from "../constants/notifications";
import { debounce } from "lodash";

export const useNotificationsV2 = (networkId = 1) => {
  const [notifications, setNotifications] = useState([]);
  const [readStatus, setReadStatus] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [pageVariables, setPageVariables] = useState([
    { first: 25, after: "" },
  ]);
  const [isPending, startTransition] = useTransition();
  const pendingNotifications = useRef([]);
  const initialUnreadCountFetched = useRef(false);

  // Query notifications
  const [{ data, fetching, error }, refetchNotifications] = useQuery({
    query: txNotificationsQuery,
    variables: {
      page: pageVariables[pageVariables.length - 1],
      filter: { networkId },
    },
  });

  // Query unread count only once
  const [{ data: unreadData }] = useQuery({
    query: unreadNotificationCountQuery,
    variables: {
      filter: { networkId },
    },
    pause: initialUnreadCountFetched.current,
  });

  // Subscribe to new notifications
  const [{ data: subscriptionData }] = useSubscription({
    query: txNotificationSubscription,
    variables: { filter: { networkId } },
  });

  // Mark notifications as read mutation
  const [, markAsRead] = useMutation(markNotificationsAsReadMutation);

  // Update unread count when initial data is fetched
  useEffect(() => {
    if (
      unreadData?.unreadNotificationCount !== undefined &&
      !initialUnreadCountFetched.current
    ) {
      setUnreadCount(unreadData.unreadNotificationCount);
      initialUnreadCountFetched.current = true;
    }
  }, [unreadData]);

  // Debounced function to process new notifications
  const processNewNotifications = useCallback(
    debounce(() => {
      if (pendingNotifications.current.length === 0) return;

      startTransition(() => {
        // Sort notifications by ID in descending order (newest first)
        const sortedNotifications = [...pendingNotifications.current].sort(
          (a, b) => b.id - a.id
        );

        setNotifications((prev) => {
          // Get existing IDs
          const existingIds = new Set(prev.map((n) => n.id));

          // Filter out notifications that already exist
          const uniqueNewNotifications = sortedNotifications.filter(
            (n) => !existingIds.has(n.id)
          );

          if (uniqueNewNotifications.length === 0) return prev;

          // Merge new notifications with existing ones, maintaining order
          const merged = [...uniqueNewNotifications, ...prev].sort(
            (a, b) => b.id - a.id
          );

          return merged;
        });

        // Update read status for new notifications
        const newReadStatus = {};
        let newUnreadCount = 0;
        sortedNotifications.forEach((notification) => {
          newReadStatus[notification.id] = notification.status;
          if (notification.status === NOTIFICATION_STATUS.UNREAD) {
            newUnreadCount++;
          }
        });

        setReadStatus((prev) => ({
          ...prev,
          ...newReadStatus,
        }));

        if (newUnreadCount > 0) {
          setUnreadCount((prev) => prev + newUnreadCount);
        }

        // Clear pending notifications
        pendingNotifications.current = [];
      });
    }, 300),
    []
  );

  // Handle new notifications from subscription
  useEffect(() => {
    if (subscriptionData?.notifications) {
      const newNotification = subscriptionData.notifications;
      pendingNotifications.current.push(newNotification);
      processNewNotifications();
    }
  }, [subscriptionData, processNewNotifications]);

  // Update notifications and read status when data changes
  useEffect(() => {
    if (data?.notifications?.edges) {
      startTransition(() => {
        if (pageVariables.length === 1) {
          // Initial load
          const newNotifications = data.notifications.edges.map(
            (edge) => edge.node
          );
          setNotifications((prev) => {
            // Preserve any newer notifications that aren't in the new data
            const existingIds = new Set(newNotifications.map((n) => n.id));
            const newerNotifications = prev.filter(
              (n) => !existingIds.has(n.id)
            );
            return [...newerNotifications, ...newNotifications].sort(
              (a, b) => b.id - a.id
            );
          });

          // Update read status and count unread notifications
          const newReadStatus = {};
          let initialUnreadCount = 0;
          newNotifications.forEach((notification) => {
            newReadStatus[notification.id] = notification.status;
            if (notification.status === NOTIFICATION_STATUS.UNREAD) {
              initialUnreadCount++;
            }
          });
          setReadStatus((prev) => ({
            ...prev,
            ...newReadStatus,
          }));

          // Only update unread count if we haven't fetched it yet
          if (!initialUnreadCountFetched.current) {
            setUnreadCount(initialUnreadCount);
          }
        } else {
          // Append new notifications for pagination
          setNotifications((prev) => {
            const newNotifications = data.notifications.edges.map(
              (edge) => edge.node
            );
            const existingIds = new Set(prev.map((n) => n.id));
            const uniqueNewNotifications = newNotifications.filter(
              (n) => !existingIds.has(n.id)
            );

            // Update read status for new notifications
            const newReadStatus = {};
            let newUnreadCount = 0;
            uniqueNewNotifications.forEach((notification) => {
              newReadStatus[notification.id] = notification.status;
              if (notification.status === NOTIFICATION_STATUS.UNREAD) {
                newUnreadCount++;
              }
            });
            setReadStatus((prev) => ({
              ...prev,
              ...newReadStatus,
            }));

            if (newUnreadCount > 0) {
              setUnreadCount((prev) => prev + newUnreadCount);
            }

            return [...prev, ...uniqueNewNotifications].sort(
              (a, b) => b.id - a.id
            );
          });
        }
        setHasMore(data.notifications.pageInfo.hasNextPage);
        setHasPrevious(data.notifications.pageInfo.hasPreviousPage);
      });
    }
  }, [data, pageVariables.length]);

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      processNewNotifications.cancel();
    };
  }, [processNewNotifications]);

  // Update loading state
  useEffect(() => {
    setIsLoading(fetching);
  }, [fetching]);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    const lastCursor =
      data?.notifications?.edges[data?.notifications?.edges.length - 1]?.cursor;
    if (lastCursor) {
      startTransition(() => {
        setPageVariables((prev) => [...prev, { first: 25, after: lastCursor }]);
      });
    }
  }, [hasMore, isLoading, data]);

  const loadPrevious = useCallback(async () => {
    if (!hasPrevious || isLoading) return;

    const firstCursor = data?.notifications?.edges[0]?.cursor;

    if (firstCursor) {
      startTransition(() => {
        setPageVariables((prev) => [
          ...prev,
          { first: 25, before: firstCursor },
        ]);
      });
    }
  }, [hasPrevious, isLoading, data]);

  // Mark notifications as read up to a specific ID
  const handleMarkAsRead = useCallback(
    async (id, filter) => {
      try {
        // Optimistic update - mark all notifications before ID as read
        const newReadStatus = { ...readStatus };
        let updatedCount = 0;

        notifications.forEach((notification) => {
          if (notification.id <= id) {
            newReadStatus[notification.id] = NOTIFICATION_STATUS.READ;
            if (readStatus[notification.id] === NOTIFICATION_STATUS.UNREAD) {
              updatedCount++;
            }
          }
        });

        setReadStatus(newReadStatus);
        setUnreadCount((prev) => Math.max(0, prev - updatedCount));

        // Call mutation
        await markAsRead({
          beforeAtId: id,
          filter: { ...filter, networkId },
        });
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
        // Revert optimistic update
        await refetchNotifications({ requestPolicy: "network-only" });
      }
    },
    [markAsRead, refetchNotifications, notifications, readStatus, networkId]
  );

  return {
    notifications,
    readStatus,
    unreadCount,
    isLoading,
    error,
    hasMore,
    hasPrevious,
    loadMore,
    loadPrevious,
    markAsRead: handleMarkAsRead,
    refetchNotifications,
    setNotifications,
  };
};
